function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : PageNavigator
// =============================================================================
// ---Third-party imports
import React from 'react';
import CursorPos from 'react-cursor-position';
// ---Framework imports
import { useState, useEffect, useSelector, useDimensions } from 'uix/hooks';
import log from 'uix/logging';
import { get, size, values, sum } from 'ldash';
// ---Local imports
import styles from './pagenav.scss';
import { addEventListener } from './cursor';
import { useViewer } from './hooks';
// ---Globals
export class CursorPosition extends CursorPos {
    addEventListeners() {
        CursorPos.prototype.addEventListeners.call(this);
        this.eventListeners.push(addEventListener(this.el, 'click', this.onClick));
    }
    constructor(...args){
        super(...args), _define_property(this, "onClick", (e)=>{
            if (this.props.onClick) {
                this.props.onClick(e);
            }
            e.stopPropagation();
        });
    }
}
let SearchHits = ({ index, width })=>{
    let viewer = useViewer();
    let activeInBookSearch = useSelector((root)=>root.data.viewer.activeInBookSearches[viewer.vid]);
    if (!activeInBookSearch) {
        return null;
    }
    let { pages, byId } = index;
    let hitPages = activeInBookSearch.searchResult.otids.filter((otid)=>byId[otid]).map((otid)=>({
            page: byId[otid],
            hits: activeInBookSearch.pageById[otid].hits
        }));
    hitPages = hitPages.map(({ page, hits })=>{
        return {
            otid: page.id,
            progressPx: page.flatIdx / (index.pages.length - 1) * width,
            termHitCount: sum(values(hits).map((v)=>v.length)),
            idx: page.flatIdx
        };
    });
    return hitPages.map((p)=>/*#__PURE__*/ _jsx("span", {
            className: styles.pagehit,
            style: {
                marginLeft: `calc(${p.progressPx}px - 0.5em)`
            },
            title: `${p.termHitCount} hits ${p.otid} ${p.idx}`,
            children: /*#__PURE__*/ _jsx("span", {
                className: styles.pagehit_inner
            })
        }, p.otid));
};
let ForwardProgress = ({ selected, index, cursored })=>{
    let { pages, byId } = index;
    let [dimensions, ref] = useDimensions({
        delta: 5
    });
    let [current, setCurrent] = useState({
        page: null
    });
    useEffect(()=>{
        if (selected.center) {
            setCurrent({
                page: byId[selected.center]
            });
        }
    }, [
        selected
    ]);
    let idx = current.page ? current.page.flatIdx : 0;
    let progressPx = idx / (index.pages.length - 1) * dimensions.width;
    let s = {
        paddingLeft: `${progressPx}px`
    };
    let title = get(cursored, 'page.pagination', get(cursored, 'idx', null));
    return /*#__PURE__*/ _jsx("div", {
        className: styles.outer,
        title: title,
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.pagenav,
            ref: ref,
            children: [
                /*#__PURE__*/ _jsx(SearchHits, {
                    index: index,
                    width: dimensions.width
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.current,
                    style: s
                })
            ]
        })
    });
};
export const PageNavigator = ({ selected, imageLocs, index, scrollToId })=>{
    let [cursored, setCursored] = useState({
        page: null,
        idx: 0
    });
    if (size(index.pages) < 2) {
        return null;
    }
    let onPositionChanged = ({ elementDimensions, isPositionOutside, position })=>{
        if (elementDimensions) {
            let idx = parseInt(Math.round((size(index.pages) - 1) * position.x / elementDimensions.width));
            if (cursored.page !== index.pages[idx]) {
                setCursored({
                    page: index.pages[idx],
                    idx
                });
            }
        }
    };
    let onClick = ()=>cursored.page ? scrollToId({
            id: cursored.page.id
        }) : null;
    return /*#__PURE__*/ _jsx(CursorPosition, {
        onPositionChanged: onPositionChanged,
        onClick: onClick,
        children: /*#__PURE__*/ _jsx(ForwardProgress, {
            selected: selected,
            index: index,
            cursored: cursored
        })
    });
};
